/**
 * Custom hook for streaming AI responses via Server-Sent Events.
 *
 * Uses fetch + ReadableStream (not EventSource) because we need
 * to send POST requests with a Bearer token.
 */

import { useState, useCallback, useRef } from "react";
import { getSessionToken } from "../lib/clerk";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

// How long to wait with nothing at all arriving before giving up. The server
// searches the family records before it sends anything, which can take a
// while, so this is generous; it only exists so a dropped connection can never
// leave the screen on "Thinking..." forever.
export const QUIET_TIMEOUT_MS = 90 * 1000;

const NO_ANSWER =
  "The answer didn't come through — the connection may have dropped. Please try again.";
const TOO_SLOW =
  "The Family Historian is taking too long to answer. Please try again in a moment.";

export function useAskAiStream() {
  const [searchSteps, setSearchSteps] = useState([]);
  const [answerText, setAnswerText] = useState("");
  const [status, setStatus] = useState("idle");
  const [matchedCount, setMatchedCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const abortRef = useRef(null);

  const askQuestion = useCallback(async (question, individualId, history) => {
    setSearchSteps([]);
    setAnswerText("");
    setStatus("searching");
    setMatchedCount(0);
    setErrorMessage("");

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    // Restarted every time something arrives; if it ever runs out, the request
    // is dropped and the member is told, instead of waiting forever. Each
    // question keeps its own timer, so one ending can't clear another's.
    let timedOut = false;
    let quietTimer = null;
    const stopQuietTimer = () => clearTimeout(quietTimer);
    const restartQuietTimer = () => {
      stopQuietTimer();
      quietTimer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, QUIET_TIMEOUT_MS);
    };
    // Whether the answer has started, and whether the server said it was done,
    // counted here rather than read back from state.
    let answerStarted = false;
    let answerChars = 0;
    let finished = false;

    try {
      restartQuietTimer();
      const token = await getSessionToken();
      if (!token) {
        stopQuietTimer();
        setStatus("error");
        setErrorMessage("You need to be signed in to use this feature.");
        return;
      }

      const body = { question };
      if (individualId) body.individual_id = individualId;
      if (history && history.length > 0) body.history = history;

      const response = await fetch(`${API_URL}/ai/ask/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const text = await response.text();
        let errorMsg = "Something went wrong. Please try again.";
        try {
          const dataMatch = text.match(/data:\s*({.*})/);
          if (dataMatch) {
            const parsed = JSON.parse(dataMatch[1]);
            errorMsg = parsed.error || errorMsg;
          }
        } catch {
          // Not parseable
        }
        stopQuietTimer();
        setStatus("error");
        setErrorMessage(errorMsg);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      let reading = true;
      while (reading) {
        const { done, value } = await reader.read();
        restartQuietTimer();
        if (done) {
          reading = false;
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          const lines = eventBlock.split("\n");
          let eventType = "";
          let eventData = "";

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              eventData = line.slice(6);
            }
          }

          if (!eventType || !eventData) continue;

          try {
            const data = JSON.parse(eventData);

            switch (eventType) {
              case "search_step":
                setSearchSteps((prev) => [...prev, data]);
                break;
              case "answer_start":
                answerStarted = true;
                setStatus("answering");
                break;
              case "answer_chunk":
                answerChars += (data.text || "").length;
                setAnswerText((prev) => prev + (data.text || ""));
                break;
              case "done":
                finished = true;
                setMatchedCount(data.matched_count || 0);
                // "Done" with no words at all would leave nothing on screen.
                if (answerChars === 0) {
                  setStatus("error");
                  setErrorMessage(NO_ANSWER);
                } else {
                  setStatus("done");
                }
                break;
              case "error":
                finished = true;
                setStatus("error");
                setErrorMessage(data.error || "Something went wrong.");
                break;
              default:
                break;
            }
          } catch {
            // Skip unparseable
          }
        }
      }

      stopQuietTimer();
      if (finished) return;
      // The stream closed without saying it was done. Whatever part of an
      // answer arrived is kept; with none at all, say so rather than leave the
      // screen on "Thinking..." forever.
      if (answerStarted && answerChars > 0) {
        setStatus("done");
      } else {
        setStatus("error");
        setErrorMessage(NO_ANSWER);
      }
    } catch (err) {
      stopQuietTimer();
      if (err.name === "AbortError") {
        // A newer question or Stop cancelled this one: nothing to report.
        if (!timedOut) return;
        setStatus("error");
        setErrorMessage(TOO_SLOW);
        return;
      }
      setStatus("error");
      setErrorMessage("Could not connect to the server. Please try again.");
    }
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStatus("idle");
  }, []);

  return {
    searchSteps,
    answerText,
    status,
    matchedCount,
    errorMessage,
    askQuestion,
    cancel,
  };
}
